
import React, { useEffect, useMemo, useState } from 'react';
import { availabilityAPI, usersAPI } from '../services/api';
import { formatDate, getTodayKualaLumpurISODate, toKualaLumpurISODate } from '../utils/dateFormat';
import { useAuth } from '../context/AuthContext';
import { useLocalization } from '../context/LocalizationContext';
import styles from '../styles/UserDashboard.module.css';
import AvailabilityCalendar from '../components/AvailabilityCalendar';

const formatTimeRange = (task) => {
  if (!task.start_time && !task.end_time) return '-';
  const start = task.start_time ? String(task.start_time).slice(0, 5) : '--:--';
  const end = task.end_time ? String(task.end_time).slice(0, 5) : '--:--';
  return `${start} - ${end}`;
};


const normalizeTaskDateISO = (value) => {
  if (!value) return '';
  if (typeof value === 'string') {
    const plainDate = value.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}$/);
    if (plainDate) return plainDate[0];
  }
  const klDate = toKualaLumpurISODate(value);
  if (klDate) return klDate;
  if (typeof value === 'string') {
    const datePrefix = value.match(/^[0-9]{4}-[0-9]{2}-[0-9]{2}/);
    if (datePrefix) return datePrefix[0];
  }
  return '';
};

const parseTaskResourceSummary = (value) => {
  if (!value) return [];
  return String(value)
    .split('||')
    .map((item) => item.trim())
    .filter(Boolean);
};

const getThreeMonthsLaterISO = (baseISO) => {
  const [year, month, day] = String(baseISO).split('-').map(Number);
  if (!year || !month || !day) return baseISO;
  const dt = new Date(year, month - 1, day);
  dt.setMonth(dt.getMonth() + 3);
  const y = dt.getFullYear();
  const m = String(dt.getMonth() + 1).padStart(2, '0');
  const d = String(dt.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

  const UserDashboardPage = () => {
    const { user } = useAuth();
    const { t } = useLocalization();
    const [tasks, setTasks] = useState([]);
    const [customEvents, setCustomEvents] = useState([]);
    const [showAllTasks, setShowAllTasks] = useState(false);
    const [todayKualaLumpur, setTodayKualaLumpur] = useState(getTodayKualaLumpurISODate());
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');

    useEffect(() => {
      const fetchData = async () => {
        try {
          setLoading(true);
          setError('');
          const response = await usersAPI.getDashboard();
          setTasks(response.data?.tasks || []);
          setTodayKualaLumpur(response.data?.todayKualaLumpur || getTodayKualaLumpurISODate());
          try {
            const customEventsResponse = await availabilityAPI.getCustomEvents();
            setCustomEvents(customEventsResponse.data || []);
          } catch (customEventsErr) {
            // Non-blocking for dashboard load.
            setCustomEvents([]);
          }
        } catch (err) {
          setError(err.response?.data?.message || t('user_dashboard_load_failed', 'Failed to load your dashboard'));
          setTasks([]);
          setCustomEvents([]);
          setTodayKualaLumpur(getTodayKualaLumpurISODate());
        } finally {
          setLoading(false);
        }
      };
      fetchData();
    }, [t]);

    const statusLabelMap = {
      pending: t('tasks_status_pending', 'Pending'),
      in_progress: t('tasks_status_in_progress', 'In Progress'),
      completed: t('tasks_status_completed', 'Completed'),
      cancelled: t('tasks_status_cancelled', 'Cancelled'),
    };

    const priorityLabelMap = {
      low: t('tasks_priority_low', 'Low'),
      medium: t('tasks_priority_medium', 'Medium'),
      high: t('tasks_priority_high', 'High'),
      urgent: t('tasks_priority_urgent', 'Urgent'),
    };

    const activeTaskCount = useMemo(
      () => tasks.filter((task) => task.status === 'in_progress' || task.status === 'pending').length,
      [tasks]
    );
    const todayTaskCount = useMemo(
      () => tasks.filter((task) => normalizeTaskDateISO(task.task_date) === todayKualaLumpur).length,
      [tasks, todayKualaLumpur]
    );
    const upcomingEventCount = useMemo(() => {
      const endISO = getThreeMonthsLaterISO(todayKualaLumpur);
      return (customEvents || []).filter((eventItem) => {
        const eventISO = normalizeTaskDateISO(eventItem.event_date);
        return !!eventISO && eventISO >= todayKualaLumpur && eventISO <= endISO;
      }).length;
    }, [customEvents, todayKualaLumpur]);
    const visibleTasks = useMemo(() => {
      const scopedTasks = showAllTasks
        ? tasks
        : tasks.filter((task) => normalizeTaskDateISO(task.task_date) === todayKualaLumpur);
      return [...scopedTasks].sort((a, b) => {
          const dateA = normalizeTaskDateISO(a.task_date);
          const dateB = normalizeTaskDateISO(b.task_date);
          if (dateA !== dateB) return dateB.localeCompare(dateA);
        const startA = (a.start_time || '00:00').slice(0, 5);
        const startB = (b.start_time || '00:00').slice(0, 5);
        if (startA !== startB) return startB.localeCompare(startA);
        return (b.id || 0) - (a.id || 0);
      });
    }, [showAllTasks, tasks, todayKualaLumpur]);

    return (
      <div className={styles.container}>
        <div className={styles.hero}>
          <div>
            <h1>{user?.role === 'supervisor' ? t('nav_supervisor_dashboard', 'Supervisor Dashboard') : t('nav_dashboard', 'Dashboard')}</h1>
            <p className={styles.subtitle}>{t('user_dashboard_subtitle', 'Relevant resources and tasks for')} {user?.first_name || user?.username}.</p>
          </div>
          <div className={styles.statsGrid}>
            <div className={styles.statCard}>
              <span className={styles.statIcon}><i className="bi bi-calendar-day" aria-hidden="true"></i></span>
              <div>
                <span className={styles.statLabel}>{t('user_dashboard_today_tasks', "Today's Tasks")}</span>
                <strong className={styles.statValue}>{todayTaskCount}</strong>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}><i className="bi bi-play-circle" aria-hidden="true"></i></span>
              <div>
                <span className={styles.statLabel}>{t('user_dashboard_active_tasks', 'Active Tasks')}</span>
                <strong className={styles.statValue}>{activeTaskCount}</strong>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}><i className="bi bi-box-seam" aria-hidden="true"></i></span>
              <div>
                <span className={styles.statLabel}>{t('dashboard_total_tasks', 'Total Tasks')}</span>
                <strong className={styles.statValue}>{tasks.length}</strong>
              </div>
            </div>
            <div className={styles.statCard}>
              <span className={styles.statIcon}><i className="bi bi-calendar-event" aria-hidden="true"></i></span>
              <div>
                <span className={styles.statLabel}>{t('user_dashboard_events', 'Events')}</span>
                <strong className={styles.statValue}>{upcomingEventCount}</strong>
              </div>
            </div>
          </div>
        </div>
        {/* Main dashboard content */}
        {loading ? (
          <p className={styles.loading}>{t('user_dashboard_loading', 'Loading your dashboard...')}</p>
        ) : error ? (
          <p className={styles.error}>{error}</p>
        ) : (
          <>
            <div className={styles.section}>
              <div className={styles.sectionHeader}>
                <h2>{showAllTasks ? t('user_dashboard_all_tasks', 'All Tasks') : t('user_dashboard_today_tasks', "Today's Tasks")}</h2>
                <div className={styles.viewButtonWrap}>
                  <button
                    type="button"
                    className={styles.viewButton}
                    onClick={() => setShowAllTasks((prev) => !prev)}
                    aria-label={showAllTasks ? t('user_dashboard_show_today_tasks', 'Show today tasks') : t('user_dashboard_show_all_tasks', 'Show all tasks')}
                    title={showAllTasks ? t('user_dashboard_show_today_tasks_title', "Show Today's Tasks") : t('user_dashboard_show_all_tasks_title', 'Show All Tasks')}
                  >
                    <i
                      className={`bi ${showAllTasks ? 'bi-calendar-day' : 'bi-list-task'}`}
                      aria-hidden="true"
                    ></i>
                  </button>
                </div>
              </div>
              {visibleTasks.length === 0 ? (
                <p className={styles.empty}>
                  {showAllTasks ? t('user_dashboard_no_tasks', 'No tasks found.') : t('user_dashboard_no_tasks_today', 'No tasks scheduled for today.')}
                </p>
              ) : (
                <div className={styles.taskList}>
                  {visibleTasks.map((task) => (
                    <div key={task.id} className={`${styles.taskCard} ${styles[`task_${task.status}`] || ''}`}>
                      <div className={styles.taskHeader}>
                        <div>
                          <h3>{task.title}</h3>
                          <p>{task.description || t('user_dashboard_no_description', 'No description provided.')}</p>
                        </div>
                        <span className={`${styles.statusBadge} ${styles[`status_${task.status}`] || ''}`}>
                          {statusLabelMap[task.status] || task.status?.replace('_', ' ')}
                        </span>
                      </div>
                      <div className={styles.taskMetaGrid}>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('reports_date', 'Date')}</span>
                          <strong>{formatDate(task.task_date)}</strong>
                        </div>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('kanban_team', 'Team')}</span>
                          <strong>{task.team_name || '-'}</strong>
                        </div>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('tasks_label_job_type', 'Job Type')}</span>
                          <strong>{task.job_type_name || '-'}</strong>
                        </div>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('tasks_label_priority', 'Priority')}</span>
                          <strong>{priorityLabelMap[task.priority] || task.priority || '-'}</strong>
                        </div>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('user_dashboard_start_end', 'Start / End')}</span>
                          <strong>{formatTimeRange(task)}</strong>
                        </div>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('kanban_members', 'Members')}</span>
                          <strong className={styles.metaValueWrap}>{task.member_names || '-'}</strong>
                        </div>
                        <div className={styles.metaItem}>
                          <span className={styles.metaLabel}>{t('kanban_resources', 'Resources')}</span>
                          <strong className={styles.metaValueWrap}>
                            {parseTaskResourceSummary(task.resource_summary).join(', ') || '-'}
                          </strong>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            <div className={styles.section}>
              <AvailabilityCalendar userId={user?.id} />
            </div>
          </>
        )}
      </div>
    );
  };

export default UserDashboardPage;
